import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Pencil,
    Trash2,
    Image as ImageIcon,
    ArrowUp,
    ArrowDown,
    X,
    Loader2,
    Save
} from 'lucide-react';
import Toast from '../Common/Toast';
import ConfirmDialog from '../Common/ConfirmDialog';
import './HeroBannerManager.css';
import API_BASE_URL from '../../config/api';

const HeroBannerManager = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: null });

    const [formData, setFormData] = useState({
        order: 0,
        isActive: true,
        image: null
    });
    const [previewUrl, setPreviewUrl] = useState(null);

    const addToast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/herobanner/all`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBanners(data.sort((a, b) => a.order - b.order));
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
            addToast('Erro ao carregar banners', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const token = localStorage.getItem('token');
        const formDataToSend = new FormData();

        if (formData.image) {
            formDataToSend.append('image', formData.image);
        }
        formDataToSend.append('order', formData.order);
        formDataToSend.append('isActive', formData.isActive);

        try {
            const url = editingBanner
                ? `${API_BASE_URL}/api/herobanner/${editingBanner.id}`
                : `${API_BASE_URL}/api/herobanner`;

            const method = editingBanner ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataToSend
            });

            if (response.ok) {
                setShowModal(false);
                resetForm();
                fetchBanners();
                addToast(editingBanner ? 'Banner atualizado com sucesso!' : 'Banner criado com sucesso!');
            } else {
                addToast('Erro ao guardar o banner', 'error');
            }
        } catch (error) {
            console.error('Error saving banner:', error);
            addToast('Ocorreu um erro técnico ao guardar', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const id = confirmState.id;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/herobanner/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                fetchBanners();
                addToast('Banner eliminado permanentemente', 'warning');
            } else {
                addToast('Erro ao eliminar banner', 'error');
            }
        } catch (error) {
            console.error('Error deleting banner:', error);
            addToast('Erro ao processar eliminação', 'error');
        } finally {
            setConfirmState({ isOpen: false, id: null });
        }
    };

    const handleEdit = (banner) => {
        setEditingBanner(banner);
        setFormData({
            order: banner.order,
            isActive: banner.isActive,
            image: null
        });
        setPreviewUrl(getImageUrl(banner.imageUrl));
        setShowModal(true);
    };

    const handleMove = async (banner, direction) => {
        const index = banners.findIndex(b => b.id === banner.id);
        const otherIndex = direction === 'up' ? index - 1 : index + 1;

        if (otherIndex < 0 || otherIndex >= banners.length) return;

        const otherBanner = banners[otherIndex];
        const newOrderCurrent = otherBanner.order;
        const newOrderOther = banner.order;

        const token = localStorage.getItem('token');

        try {
            const updateOrder = async (b, order) => {
                const fd = new FormData();
                fd.append('order', order);
                fd.append('isActive', b.isActive);

                return fetch(`${API_BASE_URL}/api/herobanner/${b.id}`, {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: fd
                });
            };

            await updateOrder(banner, newOrderCurrent);
            await updateOrder(otherBanner, newOrderOther);

            fetchBanners();
            addToast(`Posição atualizada`, 'info');
        } catch (error) {
            addToast('Erro ao reordenar', 'error');
        }
    };

    const resetForm = () => {
        setFormData({
            order: banners.length,
            isActive: true,
            image: null
        });
        setEditingBanner(null);
        setPreviewUrl(null);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData({ ...formData, image: file });

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return null;
        if (imageUrl.startsWith('http')) return imageUrl;
        return `${API_BASE_URL}${imageUrl.startsWith('/') ? imageUrl : '/' + imageUrl}`;
    };

    if (loading) {
        return (
            <div className="loading-container">
                <Loader2 className="spinner" size={48} />
                <p>A sincronizar com a central de banners...</p>
            </div>
        );
    }

    return (
        <div className="hero-banner-manager">
            {/* Toasts Container */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        {...toast}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmState.isOpen}
                title="Eliminar Banner"
                message="Esta ação é irreversível. O banner será remover permanentemente da homepage."
                onConfirm={handleDelete}
                onCancel={() => setConfirmState({ isOpen: false, id: null })}
            />

            <header className="manager-header">
                <div className="header-content">
                    <h1>Hero Banners</h1>
                    <p className="header-subtitle">Gestão de impacto visual da homepage</p>
                </div>
                <button className="btn-add" onClick={() => { resetForm(); setShowModal(true); }}>
                    <Plus size={20} /> Adicionar Banner
                </button>
            </header>

            {banners.length === 0 ? (
                <div className="empty-state glass-card">
                    <ImageIcon className="empty-icon" size={64} />
                    <h3>A galeria está vazia</h3>
                    <p>Comece por adicionar o primeiro banner de destaque</p>
                    <button className="btn-add" onClick={() => setShowModal(true)}>
                        <Plus size={20} /> Criar Primeiro Banner
                    </button>
                </div>
            ) : (
                <div className="banners-grid">
                    {banners.map((banner, index) => (
                        <div key={banner.id} className="banner-card">
                            <div className="banner-image-wrapper">
                                <img
                                    src={getImageUrl(banner.imageUrl)}
                                    alt="Hero Preview"
                                    className="banner-image"
                                />
                                <div className="banner-overlay">
                                    <button
                                        className="overlay-btn edit"
                                        onClick={() => handleEdit(banner)}
                                        title="Configurações"
                                    >
                                        <Pencil size={20} />
                                    </button>
                                    <button
                                        className="overlay-btn delete"
                                        onClick={() => setConfirmState({ isOpen: true, id: banner.id })}
                                        title="Remover"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                            <div className="banner-info">
                                <div className="banner-meta">
                                    <div className="order-badge">
                                        <span className="label">#{banner.order}</span>
                                    </div>
                                </div>
                                <div className="sorting-controls">
                                    <button
                                        className="sort-btn"
                                        onClick={() => handleMove(banner, 'up')}
                                        disabled={index === 0}
                                        title="Mover para cima"
                                    >
                                        <ArrowUp size={16} />
                                    </button>
                                    <button
                                        className="sort-btn"
                                        onClick={() => handleMove(banner, 'down')}
                                        disabled={index === banners.length - 1}
                                        title="Mover para baixo"
                                    >
                                        <ArrowDown size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowModal(false); resetForm(); } }}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <header className="modal-header">
                            <div>
                                <h2>{editingBanner ? 'Configurar Banner' : 'Publicar Hero Banner'}</h2>
                                <p className="header-subtitle">Visualize e ajuste as propriedades do elemento visual</p>
                            </div>
                            <button
                                className="modal-close"
                                onClick={() => { setShowModal(false); resetForm(); }}
                            >
                                <X size={24} />
                            </button>
                        </header>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-section-group">
                                <div className="section-label">
                                    <ImageIcon size={16} />
                                    <span>Conteúdo Visual</span>
                                </div>
                                <div className="form-section">
                                    <div className="image-upload-area">
                                        {previewUrl ? (
                                            <div className="image-preview premium-preview">
                                                <img src={previewUrl} alt="Preview" />
                                                <div className="preview-overlay">
                                                    <button
                                                        type="button"
                                                        className="btn-change-image"
                                                        onClick={() => document.getElementById('imageInput').click()}
                                                    >
                                                        <Pencil size={18} /> Substituir Media
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label htmlFor="imageInput" className="upload-label premium-label">
                                                <div className="upload-glow" />
                                                <div className="upload-icon-wrapper">
                                                    <Plus className="upload-icon" size={40} />
                                                </div>
                                                <span>Selecionar Asset de Design</span>
                                                <small>Resolução Ideal: 21:9 (1920x820px)</small>
                                            </label>
                                        )}
                                        <input
                                            id="imageInput"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            required={!editingBanner}
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section-group split">
                                <div className="section-label">
                                    <Save size={16} />
                                    <span>Configurações</span>
                                </div>
                                <div className="form-row">
                                    <div className="form-section">
                                        <label className="form-label">Sequência</label>
                                        <div className="numeric-input-wrapper">
                                            <input
                                                type="number"
                                                className="form-input"
                                                value={formData.order}
                                                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                                min="0"
                                            />
                                        </div>
                                        <small className="form-hint">Index de prioridade no carrossel</small>
                                    </div>

                                    <div className="form-section">
                                        <label className="form-label">Estado Público</label>
                                        <label className="toggle-switch premium-toggle">
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                            />
                                            <span className="toggle-slider"></span>
                                            <span className="toggle-label">
                                                {formData.isActive ? 'Live' : 'Draft'}
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <footer className="modal-actions premium-actions">
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit premium-submit" disabled={submitting}>
                                    {submitting ? (
                                        <Loader2 className="spinner" size={18} />
                                    ) : (
                                        <>
                                            {editingBanner ? 'Atualizar' : 'Publicar'}
                                        </>
                                    )}
                                </button>
                            </footer>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HeroBannerManager;